import api from '@/lib/api';

/**
 * One-shot candidate fetch with a session cache.
 *
 * `GET /candidates/{id}/full` returns the candidate plus activity, client
 * feedback, notes, snapshot and edit log, gathered concurrently server-side.
 * It costs about the same as the single candidate call did, so the profile view
 * pays one network round trip instead of six — which is the whole of what you
 * felt when opening a profile in a new tab (owner, 27 Jun 2026).
 *
 * The cache is what makes a row click instant: the list prefetches the rows
 * either side of the cursor, and both the rail and the standalone page read
 * from the same map.
 */
const _cache = new Map();   // id -> { at: epoch ms, data }
const _inflight = new Map();
const CACHE_MAX = 60;

function _remember(id, payload) {
  if (_cache.has(id)) _cache.delete(id);
  _cache.set(id, { at: Date.now(), data: payload });
  while (_cache.size > CACHE_MAX) _cache.delete(_cache.keys().next().value);
}

export function peekCandidateFull(id) {
  const hit = id ? _cache.get(id) : null;
  return hit ? hit.data : null;
}

export function invalidateCandidateFull(id) {
  if (id) _cache.delete(id);
}

export function prefetchCandidateFull(id) {
  if (!id || _cache.has(id) || _inflight.has(id)) return;
  fetchCandidateFull(id).catch(() => {});
}

const _URL_ID = /\/app\/candidates\/([0-9a-fA-F-]{8,})(?:[/?#]|$)/;

/**
 * Start the candidate fetch the moment the bundle parses, in parallel with
 * /api/bootstrap.
 *
 * Measured on a cold new tab (owner, 27 Jun 2026 — "opening a profile in a new
 * tab takes a lot of time"): bundle at 0.1s, /api/bootstrap answers at 3.9s,
 * the route's chunks land at 4.1s, and only then does the profile call go out —
 * so it returned at 8.2s. None of that waiting was necessary: the cookie is
 * already on the request, so the profile can be in flight from 0.2s and be
 * sitting in this cache before the page ever mounts.
 */
export function primeFromUrl() {
  try {
    const m = _URL_ID.exec(window.location.pathname);
    if (m) prefetchCandidateFull(m[1]);
  } catch { /* no window */ }
}

/**
 * `maxAge` (ms) is how old a cached entry may be and still be served without a
 * network call. It exists for arrow-key navigation in the sourcing rail: the
 * profile pane used to pass `force: true` whenever it found a cache hit, which
 * threw away the prefetch it had just paid for and put a second /full call on
 * every keypress. A row prefetched moments ago is not stale.
 */
export function fetchCandidateFull(id, { force = false, maxAge = Infinity } = {}) {
  const hit = _cache.get(id);
  if (hit && !force && Date.now() - hit.at <= maxAge) return Promise.resolve(hit.data);
  // Deliberately no AbortController: the in-flight promise is shared between
  // the prefetcher, the rail and the standalone page, so one caller unmounting
  // must not cancel the request the others are waiting on. (React 18 mounts
  // effects twice in dev, which is exactly how that bites.)
  if (_inflight.has(id)) return _inflight.get(id);
  const p = api.get(`/candidates/${id}/full`)
    .then(r => { _remember(id, r.data); return r.data; })
    .finally(() => _inflight.delete(id));
  _inflight.set(id, p);
  return p;
}
